import { useCallback, useRef, useState } from 'react';

type UseMapViewToggleParams = {
  isMobile: boolean;
};

type UseMapViewToggleReturn = {
  isMapView: boolean;
  setMapView: (showMap: boolean) => void;
  showMap: boolean;
};

/**
 * Manages the mobile map/list toggle and scroll position preservation.
 *
 * Replaces from SplitMapTemplateClass:
 *
 * - `this.state.hideMobileRightSidebar` (boolean) → `isMapView` state
 * - `this.LAST_SCROLL_TOP` (instance field) → `savedScrollTop` ref
 * - `setMapView(isMapView)` — saves scroll position before showing map,
 *    restores it when returning to list view. Behavior is identical.
 * - `getShowMap()` — derived value: on desktop always true, on mobile
 *    reflects the toggle state. Now returned as `showMap`.
 */
export function useMapViewToggle({ isMobile }: UseMapViewToggleParams): UseMapViewToggleReturn {
  const [isMapView, setIsMapView] = useState(false);
  const savedScrollTop = useRef(0);

  const setMapView = useCallback((showMap: boolean) => {
    if (!showMap) {
      setIsMapView(showMap);
      window.scrollTo(0, savedScrollTop.current);
    } else {
      savedScrollTop.current = document.scrollingElement?.scrollTop ?? 0;
      setIsMapView(showMap);
    }
  }, []);

  const showMap = isMobile ? isMapView : true;

  return { isMapView, setMapView, showMap };
}
